import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, computed, effect, inject, signal } from '@angular/core';
import { GetScaleAccountProgress } from 'erm-sdk';
import {
  ScaleProgressNormalizerService,
  HistoryItem,
  HistoryStatus,
} from '../services/scale-progress-normalizer.service';

type Order = 'asc' | 'desc';

@Component({
  standalone: true,
  imports: [CommonModule],
  providers: [ScaleProgressNormalizerService],
  selector: 'app-scale-progress-events',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
@if (progressSignal()) {
  <div class="tw-flex tw-flex-col tw-gap-3 tw-h-full">

    <!-- Top bar: filters/actions -->
    <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-justify-between">
      <div class="tw-flex tw-items-center tw-gap-2">
        <input
          type="search"
          placeholder="Search by text, type or status…"
          class="tw-input tw-input-bordered tw-input-sm tw-w-64 tw-rounded-lg tw-border tw-border-slate-300 tw-px-3 tw-py-1 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500"
          [value]="query()"
          (input)="onQueryChange($event)"
        />

        <select
          class="tw-select tw-select-sm tw-rounded-lg tw-border tw-border-slate-300 tw-px-2 tw-py-1 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500"
          [value]="statusFilter()"
          (change)="onStatusFilterChange($event)"
        >
          <option value="">All status</option>
          @for (s of statusOptions; track s) {
            <option [value]="s">{{ s[0].toUpperCase() + s.slice(1) }}</option>
          }
        </select>

        <select
          class="tw-select tw-select-sm tw-rounded-lg tw-border tw-border-slate-300 tw-px-2 tw-py-1 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500"
          [value]="typeFilter()"
          (change)="onTypeFilterChange($event)"
        >
          <option value="">All types</option>
          @for (t of typeOptions(); track t) {
            <option [value]="t">{{ t }}</option>
          }
        </select>

        <button
          class="tw-text-xs tw-rounded-lg tw-border tw-border-slate-300 tw-px-3 tw-py-1 hover:tw-bg-slate-50"
          (click)="clearFilters()"
          title="Clear filters"
        >
          Clear
        </button>
      </div>

      <div class="tw-flex tw-items-center tw-gap-2">
        <span class="tw-text-xs tw-text-slate-500">
          {{ filtered().length }} of {{ items().length }} events
        </span>
        <button
          class="tw-text-xs tw-rounded-lg tw-border tw-border-slate-300 tw-px-3 tw-py-1 hover:tw-bg-slate-50"
          (click)="toggleOrder()"
          title="Toggle ordering"
        >
          {{ order() === 'desc' ? 'Newest first' : 'Oldest first' }}
        </button>
      </div>
    </div>

    <!-- List -->
    <div class="tw-flex tw-flex-col tw-gap-2" style="scrollbar-gutter: stable;">
      @for (ev of paged(); track ev.id) {
        <div class="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm tw-p-3 tw-flex tw-gap-3">
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-w-6">
            <span class="tw-w-3 tw-h-3 tw-rounded-full" [ngClass]="statusDotClass(ev.status)"></span>
            <span class="tw-w-[2px] tw-grow tw-bg-slate-200"></span>
          </div>

          <div class="tw-flex-1 tw-min-w-0">
            <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <div class="tw-min-w-0 tw-flex tw-flex-col tw-gap-1">
                <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                  <span class="tw-font-medium tw-text-slate-800">{{ ev.title }}</span>
                  <span class="tw-text-[10px] tw-uppercase tw-tracking-wide tw-rounded-md tw-border tw-border-slate-200 tw-px-2 tw-py-[2px] tw-text-slate-600">
                    {{ ev.type }}
                  </span>
                  @if (ev.details) {
                    <span class="tw-text-xs tw-text-slate-500">— {{ ev.details }}</span>
                  }
                </div>
                <div class="tw-text-xs tw-text-slate-500 tw-flex tw-gap-2 tw-flex-wrap">
                  <span title="{{ ev.when | date:'medium' }}">
                    {{ timeAgo(ev.when) }}
                  </span>
                  <span>•</span>
                  <span>Status: {{ ev.status }}</span>
                </div>
              </div>

              <div class="tw-flex tw-items-center tw-gap-2 tw-min-w-40">
                <button
                  class="tw-text-xs tw-rounded-lg tw-border tw-border-slate-300 tw-px-2 tw-py-1 hover:tw-bg-slate-50"
                  (click)="toggleExpand(ev.id)"
                >
                  {{ expandedId() === ev.id ? 'Hide' : 'Details' }}
                </button>
                <button
                  class="tw-text-xs tw-rounded-lg tw-border tw-border-slate-300 tw-px-2 tw-py-1 hover:tw-bg-slate-50"
                  (click)="copyRaw(ev)"
                  title="Copy raw event (JSON)"
                >
                  Copy JSON
                </button>
              </div>
            </div>

            @if (expandedId() === ev.id) {
              <div class="tw-mt-2 tw-rounded-lg tw-bg-slate-50 tw-border tw-border-slate-200 tw-p-2 tw-text-xs tw-overflow-x-auto">
                <pre class="tw-whitespace-pre-wrap tw-break-words">{{ ev.raw | json }}</pre>
              </div>
            }
          </div>
        </div>
      }

      @if (paged().length === 0) {
        <div class="tw-flex tw-items-center tw-justify-center tw-h-40 tw-text-slate-500">
          No events match the current filters.
        </div>
      }
    </div>

    <!-- Simple pagination -->
    <div class="tw-flex tw-justify-end tw-items-center tw-gap-2">
      <button
        class="tw-text-xs tw-rounded-lg tw-border tw-border-slate-300 tw-px-3 tw-py-1 hover:tw-bg-slate-50 disabled:tw-opacity-40"
        (click)="prevPage()"
        [disabled]="page() === 1"
      >
        Previous
      </button>
      <span class="tw-text-xs tw-text-slate-600">
        Page {{ page() }} / {{ totalPages() }}
      </span>
      <button
        class="tw-text-xs tw-rounded-lg tw-border tw-border-slate-300 tw-px-3 tw-py-1 hover:tw-bg-slate-50 disabled:tw-opacity-40"
        (click)="nextPage()"
        [disabled]="page() >= totalPages()"
      >
        Next
      </button>
    </div>
  </div>
}
@else {
  <div class="tw-flex tw-items-center tw-justify-center tw-h-full">
    <p>No progress provided</p>
  </div>
}
`,
})
export class ScaleProgressEventsComponent implements OnChanges {
  private readonly normalizer = inject(ScaleProgressNormalizerService);

  @Input({ required: true }) progress!: GetScaleAccountProgress | null;

  // Options
  readonly pageSize = signal(5);
  readonly order = signal<Order>('desc');

  // Filters/UI state
  readonly query = signal<string>('');
  readonly statusFilter = signal<'' | HistoryStatus>('');
  readonly typeFilter = signal<string>('');
  readonly expandedId = signal<number | null>(null);
  readonly page = signal(1);

  // Data signals
  readonly progressSignal = signal<GetScaleAccountProgress | null>(null);
  readonly items = signal<HistoryItem[]>([]);
  readonly typeOptions = signal<string[]>([]);

  // Static options for the Status select
  readonly statusOptions: HistoryStatus[] = ['success', 'warning', 'error', 'info'];

  // Computeds
  readonly filtered = computed(() => {
    const query = this.query().trim().toLowerCase();
    const statusFilter = this.statusFilter();
    const typeFilter = this.typeFilter();
    const data = this.items();

    return data.filter((item) => {
      if (statusFilter && item.status !== statusFilter) {
        return false;
      }
      if (typeFilter && item.type !== typeFilter) {
        return false;
      }
      if (!query) {
        return true;
      }

      const searchableText = [
        item.title,
        item.type,
        item.details ?? '',
        JSON.stringify(item.raw ?? {}),
      ]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  });

  readonly sorted = computed(() => {
    const sortedArray = [...this.filtered()];
    sortedArray.sort((a, b) => a.when.getTime() - b.when.getTime());
    if (this.order() === 'desc') {
      sortedArray.reverse();
    }
    return sortedArray;
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.sorted().length / this.pageSize()))
  );

  readonly paged = computed(() => {
    const currentPage = Math.min(this.page(), this.totalPages());
    const start = (currentPage - 1) * this.pageSize();
    return this.sorted().slice(start, start + this.pageSize());
  });

  constructor() {
    // Reset page when filters change
    effect(() => {
      this.query();
      this.statusFilter();
      this.typeFilter();
      this.page.set(1);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('progress' in changes) {
      const p: GetScaleAccountProgress | null = changes['progress']?.currentValue ?? null;
      this.progressSignal.set(p);

      const items = this.normalizer.buildHistoryList(p);
      this.items.set(items);

      const types = Array.from(new Set(items.map((i) => i.type))).sort();
      this.typeOptions.set(types);
    }
  }

  // ===== UI handlers =====
  onQueryChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.query.set(target?.value ?? '');
  }

  onStatusFilterChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = (target?.value ?? '') as '' | HistoryStatus;
    this.statusFilter.set(value);
  }

  onTypeFilterChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    this.typeFilter.set(target?.value ?? '');
  }

  toggleOrder(): void {
    this.order.set(this.order() === 'desc' ? 'asc' : 'desc');
  }

  toggleExpand(id: number): void {
    this.expandedId.set(this.expandedId() === id ? null : id);
  }

  clearFilters(): void {
    this.query.set('');
    this.statusFilter.set('');
    this.typeFilter.set('');
  }

  prevPage(): void {
    this.page.update((p) => Math.max(1, p - 1));
  }

  nextPage(): void {
    this.page.update((p) => Math.min(this.totalPages(), p + 1));
  }

  async copyRaw(ev: HistoryItem): Promise<void> {
    const json = JSON.stringify(ev.raw ?? {}, null, 2);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(json);
        return;
      }
      // Fallback for environments without Clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = json;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    catch {
      // optionally show a toast
    }
  }

  statusDotClass(status: HistoryStatus) {
    return {
      'tw-bg-emerald-500': status === 'success',
      'tw-bg-amber-500': status === 'warning',
      'tw-bg-rose-500': status === 'error',
      'tw-bg-slate-400': status === 'info',
    };
  }

  // Time helper kept intentionally simple (no dependency on Intl.RelativeTimeFormat)
  timeAgo(date: Date): string {
    return date.toLocaleString();
  }
}
